import { ColorLens } from "@mui/icons-material";
import { Box, Button, Divider, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { useStore } from "@priolo/jon";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import querySo from "stores/route/query";
import { URL_PAR, URL_PAR_PLANNER_LAYER, URL_PAR_PLANNER_SESSION } from "stores/route/utils/types";
import Paragraph from "../form/Paragraph";
import Modal from "../Modal";
import Chip from "./Chip";




interface Props {
	sx?: any
}

const SelectorPlannerShowH: FunctionComponent<Props> = ({
	sx,
}) => {

	// STORE
	useStore(querySo)

	// HOOKs
	const [isOpen, setIsOpen] = useState(false)
	const [colorsUrlId, structUrlId, sessionUrl, layerUrl] = useMemo(() => querySo.getSearchUrl([
		URL_PAR.PLANNER_COLORS,
		URL_PAR.PLANNER_STRUCT,
		URL_PAR.PLANNER_SESSION,
		URL_PAR.PLANNER_LAYER,
	]), [querySo.state.queryUrl])
	const [colorsSelId, setColorsSelId] = useState<string>(colorsUrlId)
	const [structSelId, setStructSelId] = useState<string>(structUrlId)
	const [sessionShow, setSessionShow] = useState(sessionUrl as URL_PAR_PLANNER_SESSION ?? URL_PAR_PLANNER_SESSION.DEFAULT)
	const [layerShow, setLayerShow] = useState(layerUrl as URL_PAR_PLANNER_LAYER ?? URL_PAR_PLANNER_LAYER.DEFAULT)


	useEffect(() => {
		if (!isOpen) return
		setColorsSelId(colorsUrlId ?? PLANNER_COLORS_TYPE.CYCLES)
		setStructSelId(structUrlId ?? PLANNER_STRUCT_TYPE.BY_GUS)
		setSessionShow((sessionUrl as URL_PAR_PLANNER_SESSION) ?? URL_PAR_PLANNER_SESSION.DEFAULT)
		setLayerShow((layerUrl as URL_PAR_PLANNER_LAYER) ?? URL_PAR_PLANNER_LAYER.DEFAULT)
	}, [isOpen])

	// HANDLER
	const handleClick = () => {
		setIsOpen(true)
	}
	const handleClose = () => {
		setIsOpen(false)
	}
	const handleConfirm = () => {
		setIsOpen(false)
		querySo.setSearchUrl([
			URL_PAR.PLANNER_COLORS, colorsSelId,
			URL_PAR.PLANNER_STRUCT, structSelId,
			URL_PAR.PLANNER_SESSION, sessionShow,
			URL_PAR.PLANNER_LAYER, layerShow,
		])
	}

	const handleClear = (e: React.MouseEvent) => {
		e.stopPropagation()
		setIsOpen(false)
		querySo.setSearchUrl([
			URL_PAR.PLANNER_COLORS, null,
			URL_PAR.PLANNER_STRUCT, null,
			URL_PAR.PLANNER_SESSION, null,
			URL_PAR.PLANNER_LAYER, null,
		])
	}

	// RENDER
	const colorSelected = PlannerColorsTypes.find(item => item.id == colorsUrlId) ?? PlannerColorsTypes.find(item => item.id == PLANNER_COLORS_TYPE.CYCLES)
	const structureSelected = PlannerStructTypes.find(item => item.id == structUrlId) ?? PlannerStructTypes.find(item => item.id == PLANNER_STRUCT_TYPE.BY_GUS)
	const label = colorSelected.chip + " | " + structureSelected.chip

	const isActive = 
		(colorsUrlId && colorsUrlId !== PLANNER_COLORS_TYPE.CYCLES) ||
		(structUrlId && structUrlId !== PLANNER_STRUCT_TYPE.BY_GUS) ||
		(sessionUrl && sessionUrl !== URL_PAR_PLANNER_SESSION.DEFAULT) ||
		(layerUrl && layerUrl !== URL_PAR_PLANNER_LAYER.DEFAULT)

	return <>
		<Chip sx={sx}
			icon={<ColorLens />}
			label={label}
			active={!!isActive}
			onClick={handleClick}
			onClear={handleClear}
			tooltip="DISPLAY STYLE"
		/>

		<Modal
			title="SELECT DISPLAY STYLE"
			icon={<ColorLens />}
			isOpen={isOpen}
			onClose={handleClose}
			maxWidth="xs"
			actionsRender={<>
				<Box sx={{ flex: "1 1 auto" }} />
				<Button onClick={handleClose}>
					CANCEL
				</Button>
				<Button onClick={handleConfirm} color="secondary">
					OK
				</Button>
			</>}
		>

			<Paragraph 
				flex={50}
				title={"COLORS"}
				subtitle={"same colors for..."}
			>
				<RadioGroup
					value={colorsSelId}
					onChange={(e, id) => setColorsSelId(id)}
				>
					{PlannerColorsTypes.map(value => (
						<FormControlLabel
							sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
							key={value.id}
							value={value.id}
							control={<Radio color="secondary" />}
							label={value.label}
						/>
					))}
				</RadioGroup>

			</Paragraph>

			<Divider sx={{ my: 1 }} />

			<Paragraph
				flex={50}
				title={"STRUCTURE"}
				subtitle={"the first level is..."}
			>
				<RadioGroup
					value={structSelId}
					onChange={(e, id) => setStructSelId(id)}
				>
					{PlannerStructTypes.map(value => (
						<FormControlLabel
							sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
							key={value.id}
							value={value.id}
							control={<Radio color="secondary" />}
							label={value.label}
						/>
					))}
				</RadioGroup>

			</Paragraph>

			<Divider sx={{ my: 1 }} />

			<Paragraph
				flex={50}
				title={"CYCLES"}
				subtitle={"If a cycle is in production"}
			>
				<RadioGroup
					value={sessionShow}
					onChange={(e, value) => setSessionShow(value as URL_PAR_PLANNER_SESSION)}
				>

					<FormControlLabel
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
						value={URL_PAR_PLANNER_SESSION.DEFAULT}
						control={<Radio color="secondary" />}
						label={"ALL CYCLES"}
					/>

					<FormControlLabel
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
						value={URL_PAR_PLANNER_SESSION.NO_PROD}
						control={<Radio color="secondary" />}
						label={"HIDE PRODUCTION CYCLES"}
					/>

				</RadioGroup>

			</Paragraph>

			<Divider sx={{ my: 1 }} />

			<Paragraph
				flex={50}
				title={"LAYERS"}
				subtitle={"If show also the disabled layers"}
			>
				<RadioGroup
					value={layerShow}
					onChange={(e, value) => setLayerShow(value as URL_PAR_PLANNER_LAYER)}
				>

					<FormControlLabel
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
						value={URL_PAR_PLANNER_LAYER.DEFAULT}
						control={<Radio color="secondary" />}
						label={"HIDE THE DISABLED"}
					/>

					<FormControlLabel
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
						value={URL_PAR_PLANNER_LAYER.ALL}
						control={<Radio color="secondary" />}
						label={"SHOW ALL"}
					/>

					<FormControlLabel
						sx={{ '& .MuiFormControlLabel-label': { fontSize: '12px', } }}
						value={URL_PAR_PLANNER_LAYER.ONLY_OCCUPIED}
						control={<Radio color="secondary" />}
						label={"SHOW ONLY OCCUPIED"}
					/>

				</RadioGroup>

			</Paragraph>

		</Modal>
	</>
}

export default SelectorPlannerShowH



export enum PLANNER_COLORS_TYPE {
	CYCLES = "cycles",
	CROP = "crop",
}
export const PlannerColorsTypes = [
	{ id: PLANNER_COLORS_TYPE.CYCLES, label: "FOR CYCLES", chip: "CYCLE" },
	{ id: PLANNER_COLORS_TYPE.CROP, label: "FOR CROPS", chip: "CROP" },
]
export type PlannerColors = typeof PlannerColorsTypes[number]



export enum PLANNER_STRUCT_TYPE {
	/** con raggruppamento su GROW-UNIT  */
	BY_GUS = "gus",
	/** con raggruppamento su PHASES */
	BY_PHASES = "phases",
}
export const PlannerStructTypes = [
	{ id: PLANNER_STRUCT_TYPE.BY_GUS, label: "BY GROW UNITS", chip: "GROW-U" },
	{ id: PLANNER_STRUCT_TYPE.BY_PHASES, label: "BY PHASES", chip: "PHASE" },
]
export type PlannerStruct = typeof PlannerStructTypes[number]
